/*
 * Option rows cascade top-down in DISPLAY order (color_first can lift a Colour row above
 * Shopify's option 1). Row 0 shows every value; a lower row shows a value only if some variant
 * has it together with the selected value of every row above - the rest are hidden and
 * disabled. A shown value with no purchasable such variant is sold out (data-unavailable).
 * snippets/product-option-picker.liquid renders the same state server-side, so applying it on
 * init changes nothing visible - keep the two in step.
 *
 * Plain functions of plain data, testable without a DOM:
 *   rows      [{ index, values }] in display order; index = Shopify option index
 *             (variant.options[index]), values = the row's values in displayed order
 *   variants  product.variants JSON
 *   selected  option values, indexed like variant.options
 */
// variant-cascade:start
const matchesRowsAbove = (variant, rows, r, selected) =>
  rows.slice(0, r).every(({ index }) => variant.options[index] === selected[index]);

const rowState = (rows, variants, selected, r) => {
  const exists = new Set();
  const available = new Set();
  for (const variant of variants) {
    if (!matchesRowsAbove(variant, rows, r, selected)) continue;
    const value = variant.options[rows[r].index];
    exists.add(value);
    if (variant.available) available.add(value);
  }
  if (r === 0) rows[0].values.forEach(value => exists.add(value));
  return { exists, available };
};

const cascadeState = (rows, variants, selected) => rows.map((row, r) => {
  const { exists, available } = rowState(rows, variants, selected, r);
  return row.values.map(value => ({ exists: exists.has(value), available: available.has(value) }));
});

const findLast = (values, test) => {
  for (let i = values.length - 1; i >= 0; i--) {
    if (test(values[i])) return values[i];
  }
};

// Row r now shows value. Each lower row keeps its value while that still exists under the rows
// above it; otherwise it takes its last available value, else its last existing one.
const resolveSelection = (rows, variants, selected, r, value) => {
  const next = [...selected];
  next[rows[r].index] = value;
  for (let k = r + 1; k < rows.length; k++) {
    const { index, values } = rows[k];
    const { exists, available } = rowState(rows, variants, next, k);
    if (exists.has(next[index])) continue;
    const pick = findLast(values, v => available.has(v)) ?? findLast(values, v => exists.has(v));
    if (pick !== undefined) next[index] = pick;
  }
  return next;
};

const findVariant = (variants, options) => variants.find(variant =>
  variant.options.length === options.length && variant.options.every((option, i) => option === options[i]));

// A variant with exactly the resolved options exists by construction. The fallbacks only cover
// an option that has no row (left out by Wholesale Club) or a value with no variants at all.
const selectVariant = (rows, variants, current, r, value) => {
  const options = resolveSelection(rows, variants, current.options, r, value);
  return findVariant(variants, options)
    || variants.find(variant => matchesRowsAbove(variant, rows, r + 1, options))
    || current;
};
// variant-cascade:end

(async () => {
    const {
      updateURLParams,
      historyPush,
      parseHTML
    } = await import(window.theme.modules.utils);

    const parseJSON = (el) => JSON.parse(el.textContent)
    const baseComponent = await import(window.theme.modules.baseComponent);

    customElements.define('product-variant-selector', class extends baseComponent.default {
      elements = {
        variantsJSON: '[data-variants-json]',
        rows: '*product-option-picker[data-option-index]'
      };

      render() {
        this.cache = {};
        this.isProductPage = this.hasAttribute('product-page');
        this.variantsData = this.$variantsJSON ? parseJSON(this.$variantsJSON) : [];
        this.variant = this.variantsData.find(v => v.id === +this.getAttribute('current-variant-id'))
          || this.variantsData[0];
        if (!this.variant) return;

        // Read the rows from the DOM, not the options JSON: a Wholesale Club 'continue' can
        // leave out values (or whole options) that the JSON still lists.
        this.rows = this.$rows.map(picker => {
          const inputs = Array.from(picker.querySelectorAll('input[type="radio"]'));
          return { index: +picker.dataset.optionIndex, values: inputs.map(input => input.value), inputs };
        });

        this.addEventListener('change', this._variantChangeHandler.bind(this));
        // Adding to cart changes what the section shows for that variant (stock, quantity),
        // so the next visit to it must refetch.
        this.on('qtyStatusUpdate', (e, data) => {
          delete this.cache[+data.variantId];
        }, false)
        this._applyState();
        this._setInitialCache();
      };

      _setInitialCache() {
        const parentSection = this.getParentSection();
        if (parentSection) this.cache[this.variant.id] = parentSection.outerHTML;
      };

      _applyState() {
        const state = cascadeState(this.rows, this.variantsData, this.variant.options);
        this.rows.forEach((row, r) => row.inputs.forEach((input, i) => {
          const { exists, available } = state[r][i];
          // A swatch's own .#form-field; in a dropdown that wraps the whole list, so take the item.
          const item = input.closest('.\\#form-custom-select-item, .\\#form-field');
          // toggleAttribute is a no-op when the state already matches, as it does on init.
          if (item) item.toggleAttribute('hidden', !exists);
          input.toggleAttribute('disabled', !exists);
          input.toggleAttribute('data-unavailable', exists && !available);
        }));
      };

      _variantChangeHandler(e) {
        if (e.detail?.synthetic) return;
        const r = this.rows.findIndex(row => row.inputs.includes(e.target));
        if (r === -1) return;

        // All synchronous: the dropper length calculator clicks a second option 250ms after the
        // first and gives up if that input is still disabled.
        this.variant = selectVariant(this.rows, this.variantsData, this.variant, r, e.target.value);
        this._syncInputs();
        this._applyState();
        this._loadVariant(this.variant);
      };

      // Check each row's radio for this.variant. The synthetic change updates the row's header
      // label (product-option-picker below) without re-entering _variantChangeHandler.
      _syncInputs() {
        this.rows.forEach(({ index, inputs }) => {
          const input = inputs.find(input => input.value === this.variant.options[index]);
          if (!input || input.checked) return;
          input.checked = true;
          input.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { synthetic: true } }));
        });
      };

      _loadVariant(variant) {
        const productURL = window.location.origin + this.getAttribute('product-url');
        const { queryURL, currentURL } = updateURLParams({ 'variant': variant.id }, this.sectionId, productURL);
        if (this.isProductPage) historyPush(currentURL);

        const cached = this.cache[variant.id];
        if (cached) {
          this.forwardEvent('variantChange', {
            html: parseHTML(cached),
            variant: variant
          });
          return;
        }

        this.forwardEvent('variantLoading');
        fetch(queryURL)
          .then(response => {
            if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
            return response.text();
          })
          .then(html => {
            this.cache[variant.id] = html;
            // Responses can land out of order (the dropper calculator sets two options 250ms
            // apart). One for a variant already moved past must not replace the current price,
            // stock and cart id.
            if (variant.id !== this.variant.id) return;
            this.forwardEvent('variantChange', {
              html: parseHTML(html),
              variant: variant
            });
          })
          .catch(() => {
            if (variant.id !== this.variant.id || this.cache[variant.id]) return;
            // No section means the loading overlay stays up over a stale cart id: let Shopify
            // render the page for this variant instead.
            const url = new URL(productURL);
            url.searchParams.set('variant', variant.id);
            window.location.href = url.href;
          });
      };
    });

    customElements.define('product-option-picker', class extends baseComponent.default {
        elements = {
            value: '[data-option-value]'
        }
        render() {
            this.addEventListener('change', e => {
                if(this.$value) {
                    this.$value.innerText = e.target.value;
                }
            })
        }
    })
  })();
